/* ==========================================================================
   UltraWheels — load-more.js
   Reveals products a few at a time while fetching whole pages from the server.

   The server paginates in pages (e.g. 9); the button reveals `data-chunk`
   products per click and parks the rest of the fetched page in a queue, so one
   request covers several clicks. The button is a real link to the next page,
   so without JS (or if a request fails) it simply navigates.
   ========================================================================== */

(function () {
  'use strict';

  class LoadMore extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-load-more-button]');
      this.progress = this.querySelector('[data-load-more-progress]');
      this.bar = this.querySelector('[data-load-more-bar]');
      this.status = this.querySelector('[data-load-more-status]');
      this.grid = document.getElementById(this.dataset.grid);
      if (!this.button || !this.grid) return;

      this.total = parseInt(this.dataset.total, 10) || 0;
      this.chunk = parseInt(this.dataset.chunk, 10) || 3;
      this.queue = [];
      this.nextUrl = this.button.getAttribute('href');
      this.loading = false;

      this.button.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      // Let modifier- and middle-clicks open the next page normally.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.reveal();
    }

    async reveal() {
      if (this.loading) return;

      if (this.queue.length === 0) {
        const ok = await this.fetchNextPage();
        if (!ok) return;
      }

      const batch = this.queue.splice(0, this.chunk);
      batch.forEach((card) => this.grid.appendChild(card));

      this.update();
      this.focusFirst(batch);
    }

    async fetchNextPage() {
      if (!this.nextUrl) return false;

      this.setBusy(true);
      const requested = this.nextUrl;

      try {
        const target = new URL(requested, window.location.origin);
        target.searchParams.set('section_id', this.dataset.sectionId);

        const response = await fetch(target.toString());
        if (!response.ok) throw new Error(response.status);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextGrid = doc.querySelector('[data-product-view]');
        if (!nextGrid) throw new Error('grid missing');

        this.queue.push(...Array.from(nextGrid.children));

        // Where the page after this one lives (absent on the last page).
        const nextButton = doc.querySelector('load-more [data-load-more-button]');
        this.nextUrl = nextButton ? nextButton.getAttribute('href') : null;

        return this.queue.length > 0;
      } catch (error) {
        // Fall back to a normal page load rather than leaving a dead button.
        window.location.href = requested;
        return false;
      } finally {
        this.loading = false;
        this.setBusy(false);
      }
    }

    update() {
      const shown = Math.min(this.grid.children.length, this.total);

      if (this.progress) {
        this.progress.textContent = (this.dataset.progressTemplate || '')
          .replace('__SHOWN__', shown)
          .replace('__TOTAL__', this.total);
        this.setStatus(this.progress.textContent);
      }

      if (this.bar && this.total > 0) {
        this.bar.style.setProperty('--load-more-progress', `${(shown / this.total) * 100}%`);
      }

      // Nothing left in the queue and no further page to fetch.
      if (this.queue.length === 0 && !this.nextUrl) {
        this.button.remove();
        this.button = null;
      }
    }

    focusFirst(batch) {
      if (batch.length === 0) return;
      const link = batch[0].querySelector('a');
      if (!link) return;
      link.setAttribute('tabindex', '-1');
      link.focus({ preventScroll: true });
    }

    setBusy(busy) {
      this.loading = busy;
      this.classList.toggle('is-loading', busy);
      if (!this.button) return;
      this.button.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (busy) {
        this.button.setAttribute('aria-disabled', 'true');
      } else {
        this.button.removeAttribute('aria-disabled');
      }
    }

    setStatus(text) {
      if (this.status) this.status.textContent = text;
    }
  }

  customElements.define('load-more', LoadMore);
})();
